import React from 'react';
import {
  TextField,
  Button,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Typography,
  IconButton,
  withStyles,
  CircularProgress,
  LinearProgress,
  Snackbar,
} from '@material-ui/core';
import { Delete as DeleteIcon, CloudUpload as UploadIcon } from '@material-ui/icons';
import { UploadHelper } from '../common/helpers/UploadHelper';
import { ApiHelper } from '../common/helpers/ApiHelper';
import { Helper } from '../common/helpers/Helper';
import { CommonStyles } from '../styles/Styles';
import Tooltip from '@material-ui/core/Tooltip';
import { StyleSheet, css } from 'aphrodite';

const styles = function(theme) {
  return {
    root: {
      padding: theme.spacing(4),
    },
    input: {
      display: 'none',
    },
    previewImage: {
      width: '100%',
      height: 140,
      objectFit: 'cover',
    },
    card: {
      maxWidth: 345,
      margin: theme.spacing(2),
    },
    uploadSection: {
      marginBottom: theme.spacing(4),
    },
    loadingContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: theme.spacing(2),
    },
    progressContainer: {
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(2),
    },
    setHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: theme.spacing(2),
    },
    setId: {
      fontSize: '0.8rem',
      color: theme.palette.text.secondary,
      marginLeft: theme.spacing(1),
    },
    errorText: {
      color: theme.palette.error.main,
    },
    successText: {
      color: theme.palette.primary.main,
    },
    warningText: {
      color: theme.palette.secondary.main,
    },
  };
};

class ImageSetManager extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      sets: [],
      currentSetName: '',
      currentImages: [],
      links: [],
      loading: false,
      uploadProgress: 0,
      message: '',
      fetchState: ApiHelper.State.READY,
      snackbarOpen: false,
      snackbarMessage: '',
      snackbarSeverity: 'info',
    };
    this.imageInputRef = null;
    
    // Bind methods
    this.handleSetNameChange = this.handleSetNameChange.bind(this);
    this.handleImageChange = this.handleImageChange.bind(this);
    this.handleAddSet = this.handleAddSet.bind(this);
    this.handleDeleteSet = this.handleDeleteSet.bind(this);
    this.handleCloseSnackbar = this.handleCloseSnackbar.bind(this);
    this.onUploadProgress = this.onUploadProgress.bind(this);
    this.onUploadComplete = this.onUploadComplete.bind(this);
    this.onError = this.onError.bind(this);
  }

  componentDidMount() {
    this.loadImageSets();
  }

  // Generate unique ID for each set
  generateSetId() {
    return 'set_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }

  // Load existing image sets from props
  loadImageSets() {
    const self = this;
    self.setState({ loading: true });
    
    // Simulate API call delay
    setTimeout(() => {
      try {
        // Load from props.cam.referenceImages
        let savedSets = [];
        if (self.props.cam && self.props.cam.referenceImages) {
          // If referenceImages is an array, use it directly
          if (Array.isArray(self.props.cam.referenceImages)) {
            savedSets = self.props.cam.referenceImages;
          } else if (typeof self.props.cam.referenceImages === 'string') {
            // If it's a string, try to parse it
            try {
              savedSets = JSON.parse(self.props.cam.referenceImages);
            } catch (parseError) {
              console.error('Error parsing referenceImages:', parseError);
              savedSets = [];
            }
          }
        }
        
        self.setState({ sets: savedSets });
        self.showSnackbar('Image sets loaded successfully', 'success');
      } catch (error) {
        console.error('Error loading image sets:', error);
        self.showSnackbar('Error loading image sets', 'error');
      } finally {
        self.setState({ loading: false });
      }
    }, 1000);
  }

  // Save sets via props callback
  saveImageSets(sets) {
    try {
      if (this.props.saveReferenceImages) {
        this.props.saveReferenceImages(sets);
      }
    } catch (error) {
      console.error('Error saving image sets:', error);
      this.showSnackbar('Error saving image sets', 'error');
    }
  }

  showSnackbar(message, severity) {
    severity = severity || 'info';
    this.setState({
      snackbarOpen: true,
      snackbarMessage: message,
      snackbarSeverity: severity,
    });
  }

  handleCloseSnackbar() {
    this.setState({ snackbarOpen: false });
  }

  handleSetNameChange(e) {
    this.setState({ currentSetName: e.target.value });
  }

  handleImageChange(e) {
    const files = Array.from(e.target.files);
    const imagePreviews = files.map((file) => {
      return {
        file: file,
        preview: URL.createObjectURL(file),
        id: 'img_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9),
        name: file.name,
      };
    });
    this.setState({ currentImages: imagePreviews });
  }

  validateSetData() {
    const { currentSetName, currentImages, sets } = this.state;
    
    if (!currentSetName.trim()) {
      this.showSnackbar('Please enter a set name', 'warning');
      return false;
    }
    
    if (currentImages.length === 0) {
      this.showSnackbar('Please select images to upload', 'warning');
      return false;
    }

    // Check if set name already exists
    const nameExists = sets.some((set) => {
      return set.name.toLowerCase() === currentSetName.trim().toLowerCase();
    });
    
    if (nameExists) {
      this.showSnackbar('A set with this name already exists', 'warning');
      return false;
    }

    return true;
  }

  handleAddSet() {
    if (!this.validateSetData()) {
      return;
    }

    const { currentSetName, currentImages, sets } = this.state;
    
    const newSet = {
      id: this.generateSetId(),
      name: currentSetName.trim(),
      images: currentImages,
      createdAt: new Date().toISOString(),
      uploadedLinks: [],
      status: 'uploading',
    };

    // Add set to state immediately to show progress
    const updatedSets = [...sets, newSet];
    this.setState({
      sets: updatedSets,
      currentSetName: '',
      currentImages: [],
      uploadProgress: 0,
    });

    // Clear file input
    if (this.imageInputRef) {
      this.imageInputRef.value = '';
    }

    // Start upload process
    this.handleUploadSet(newSet);
  }

  onUploadProgress(percent, setId) {
    this.setState({
      uploadProgress: percent,
      message: `Uploading: ${percent}%`,
      fetchState: ApiHelper.State.LOADING
    });

    // Update specific set progress if setId provided
    if (setId) {
      const { sets } = this.state;
      const updatedSets = sets.map((set) => {
        if (set.id === setId) {
          return {
            ...set,
            uploadProgress: percent
          };
        }
        return set;
      });
      this.setState({ sets: updatedSets });
    }
  }

  onUploadComplete(uploadResults, setId, totalImages) {
    const { sets, links } = this.state;
    
    // Extract all links from the upload results array
    const newLinks = uploadResults.map(result => result.link);
    
    this.setState({
      links: [...links, ...newLinks], // Add all new links
      message: 'Upload successful',
      fetchState: ApiHelper.State.READY
    });
  
    // Update specific set status
    if (setId) {
      const updatedSets = sets.map((set) => {
        if (set.id === setId) {
          return {
            ...set,
            status: 'completed',
            uploadedLinks: [...(set.uploadedLinks || []), ...newLinks], // Add all new links
            uploadProgress: 100
          };
        }
        return set;
      });
      
      this.setState({ sets: updatedSets }, () => {
        // Check if all sets are completed (comparing completed sets count)
        const completedSets = updatedSets.filter(set => set.status === 'completed');
        
        if (completedSets.length === this.state.sets.length) {
          this.saveImageSets(updatedSets);
        }
        
        // Show success message for this specific set
        const setObj = sets.find((s) => s.id === setId);
        this.showSnackbar(`Set "${setObj ? setObj.name : 'Unknown'}" uploaded successfully`, 'success');
      });
    }
  }
  onError(err, setId) {
    this.setState({
      message: Helper.getErrorMsg(err),
      fetchState: ApiHelper.State.ERROR
    });

    // Update specific set status
    if (setId) {
      const { sets } = this.state;
      const updatedSets = sets.map((set) => {
        if (set.id === setId) {
          return {
            ...set,
            status: 'error',
            error: Helper.getErrorMsg(err)
          };
        }
        return set;
      });
      this.setState({ sets: updatedSets });
      this.showSnackbar(`Error uploading set: ${Helper.getErrorMsg(err)}`, 'error');
    }
  }

  handleUploadSet(setToUpload) {
    const { images, id: setId } = setToUpload;
    
    if (images.length === 0) {
      this.onError('No images to upload', setId);
      return;
    }
  
    try {
      let completedUploads = 0;
      const totalImages = images.length;
      const uploadResults = []; // Store all upload results
  
      images.forEach((imgObj, index) => {
        const { file } = imgObj;
        const options = {
          type: 'media',
          ext: file.name.split('.').pop(),
        };
  
        // Obtain signed upload URL
        UploadHelper.fetchUploadUrl({}, options, (err, signedUploadInfo) => {
          if (err) {
            console.error('Error fetching upload URL:', err);
            this.onError(err, setId);
            return;
          }
  
          // Convert file to arrayBuffer and blob
          const reader = new FileReader();
          reader.onload = (e) => {
            const arrayBuffer = e.target.result;
            const blob = new Blob([arrayBuffer], { type: file.type });
            
            const uploadOptions = {
              pickerResult: blob,
              signedUploadInfo: signedUploadInfo,
              onEvent: (type, percent) => {
                if (type === 'progress') {
                  // Calculate overall progress for the set
                  const overallProgress = Math.round(
                    ((completedUploads * 100) + percent) / totalImages
                  );
                  this.onUploadProgress(overallProgress, setId);
                } else if (type === 'load') {
                  // Store this upload's result
                  uploadResults[index] = signedUploadInfo;
                  completedUploads++;
                  
                  if (completedUploads === totalImages) {
                    // Pass all upload results instead of just the last one
                    this.onUploadComplete(uploadResults, setId, totalImages);
                  }
                } else if (type === 'error') {
                  this.onError('Upload failed. You can try again', setId);
                }
              }
            };
            
            UploadHelper.uploadFormDataWithProgress(uploadOptions);
          };
          
          reader.onerror = () => {
            this.onError('Error reading file', setId);
          };
          
          reader.readAsArrayBuffer(file);
        });
      });
    } catch (error) {
      console.error('Error in handleUploadSet:', error);
      this.onError(error, setId);
    }
  }

  handleDeleteSet(setId) {
    const { sets } = this.state;
    const setToDelete = sets.find((set) => set.id === setId);
    
    if (setToDelete) {
      // Clean up preview URLs to prevent memory leaks
      setToDelete.images.forEach((img) => {
        if (img.preview) {
          URL.revokeObjectURL(img.preview);
        }
      });

      const updatedSets = sets.filter((set) => set.id !== setId);
      this.setState({ sets: updatedSets });
      this.saveImageSets(updatedSets);
      this.showSnackbar(`Set "${setToDelete.name}" deleted successfully`, 'info');
    }
  }

  renderSetStatus(set) {
    const { classes } = this.props;
    
    if (set.status === 'uploading') {
      return (
        <div className={classes.progressContainer}>
          <Typography variant="body2" color="textSecondary">
            Uploading... {set.uploadProgress || 0}%
          </Typography>
          <LinearProgress 
            variant="determinate" 
            value={set.uploadProgress || 0} 
          />
        </div>
      );
    } else if (set.status === 'error') {
      return (
        <Typography 
          variant="body2" 
          className={classes.errorText}
        >
          Error: {set.error}
        </Typography>
      );
    } else if (set.status === 'completed') {
      return (
        <Typography 
          variant="body2" 
          className={classes.successText}
        >
          ✓ Upload completed
        </Typography>
      );
    }
    return null;
  }

  render() {
    const {
      sets,
      currentSetName,
      currentImages,
      loading,
      snackbarOpen,
      snackbarMessage,
    } = this.state;
    const { classes } = this.props;

    if (loading) {
      return (
        <div className={classes.loadingContainer}>
          <CircularProgress />
          <Typography variant="h6" style={{ marginLeft: 16 }}>
            Loading image sets...
          </Typography>
        </div>
      );
    }

    return (
      <div className={classes.root}>
        
        <div className={classes.uploadSection}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4}>
              <TextField
                label="Set Name"
                variant="outlined"
                fullWidth
                value={currentSetName}
                onChange={this.handleSetNameChange}
                placeholder="Enter a unique set name"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <input
                accept="image/*"
                className={classes.input}
                id="upload-images"
                multiple
                type="file"
                onChange={this.handleImageChange}
                ref={(ref) => { this.imageInputRef = ref; }}
              />
              {/* <label htmlFor="upload-images">
                <Button
                  variant="contained"
                  color="primary"
                  component="span"
                >
                  <UploadIcon style={{ marginRight: 8 }} />
                  Select Images
                </Button>
              </label> */}
              <Tooltip title={'Upload Images'}>
              <label htmlFor="upload-images">
                <div className={css(CommonStyles.headerMemberOuterMobileStyle)}>
                  {
                    <div
                      className={css(CommonStyles.headerMemberAddContainer)}>
                      <UploadIcon style={{ marginRight: 8 }} />
                    </div>
                  }
                </div>
                </label>
              </Tooltip>
            </Grid>
            <Grid item xs={12} sm={4}>
              {/* <Button
                variant="contained"
                color="secondary"
                onClick={this.handleAddSet}
                disabled={!currentSetName.trim() || currentImages.length === 0}
              >
                Add Set
              </Button> */}

              <Tooltip title={'Add Set'}>
                <div className={css(CommonStyles.headerMemberOuterMobileStyle)}>
                  {
                    <div  disabled={!currentSetName.trim() || currentImages.length === 0}
                      className={css(CommonStyles.headerMemberAddContainer)} onClick={this.handleAddSet}>
                      Add Set
                    </div>
                  }
                </div>
              </Tooltip>
            </Grid>
          </Grid>
        </div>

        {/* Preview Selected Images */}
        {currentImages.length > 0 && (
          <div>
            <Typography variant="h6" gutterBottom>
              Preview Selected Images ({currentImages.length}):
            </Typography>
            <Grid container spacing={2}>
              {currentImages.map((img, index) => (
                <Grid item xs={12} sm={3} key={img.id}>
                  <Card className={classes.card}>
                    <CardMedia
                      component="img"
                      alt={`preview-${index}`}
                      height="140"
                      image={img.preview}
                      title={img.name}
                    />
                    <CardContent>
                      <Typography 
                        variant="body2" 
                        color="textSecondary"
                        noWrap
                      >
                        {img.name}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </div>
        )}

        {/* Uploaded Sets */}
        <div style={{ marginTop: '40px' }}>
          <Typography variant="h5" gutterBottom>
            Uploaded Sets ({sets.length}):
          </Typography>
          {sets.length === 0 ? (
            <Typography color="textSecondary">
              No sets uploaded yet. Create your first image set above.
            </Typography>
          ) : (
            sets.map((set) => (
              <div
                key={set.id}
                style={{
                  marginBottom: '30px',
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  padding: '16px'
                }}
              >
                <div className={classes.setHeader}>
                  <div>
                    <Typography variant="h6">
                      {set.name}
                      <span className={classes.setId}>
                        ID: {set.id}
                      </span>
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Created: {new Date(set.createdAt).toLocaleString()}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Images: {set.images.length}
                    </Typography>
                  </div>
                  <IconButton
                    aria-label="delete"
                    onClick={() => this.handleDeleteSet(set.id)}
                    disabled={set.status === 'uploading'}
                  >
                    <DeleteIcon color="error" />
                  </IconButton>
                </div>

                {this.renderSetStatus(set)}

                <Grid 
                  container 
                  spacing={2} 
                  style={{ marginTop: '8px' }}
                >
                  {set.images.map((img, index) => (
                    <Grid 
                      item 
                      xs={12} 
                      sm={3} 
                      key={img.id || index}
                    >
                      <Card className={classes.card}>
                        <CardMedia
                          component="img"
                          alt={`set-${set.id}-img-${index}`}
                          height="140"
                          image={set.uploadedLinks[index] ||  img.preview}
                          title={img.name || `Image ${index + 1}`}
                        />
                        <CardContent>
                          <Typography 
                            variant="body2" 
                            color="textSecondary"
                            noWrap
                          >
                            {img.name || `Image ${index + 1}`}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </div>
            ))
          )}
        </div>

        {/* Snackbar */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={this.handleCloseSnackbar}
          message={snackbarMessage}
        />
      </div>
    );
  }
}

export default withStyles(styles)(ImageSetManager);